import { useEffect } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { MONEY_REALTIME_ENABLED } from '@/core/release-capabilities';
import { hasBusinessContext } from '@/core/auth/has-business-context';
import { useAuthStore } from '@/state/stores/auth.store';
import {
  createMoneyRealtimeInvalidationHandler,
  MONEY_REALTIME_FRESHNESS_MS,
  MONEY_REALTIME_INVALIDATION_EVENT,
  revalidateActiveMoneyQueries,
} from './money-realtime-invalidation';

const REALTIME_PATH = '/socket.io/';
const REALTIME_ENDPOINT = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

function refreshSocketAuth(socket: Socket): void {
  // The access token is memory-only. Refresh before every reconnect so a
  // stale handshake cannot keep a browser eligible after session rotation.
  socket.auth = { token: useAuthStore.getState().accessToken ?? '' };
}

/**
 * Invisible application-level lifecycle for the accepted Money invalidation
 * foundation. The server owns topic selection, tenant/branch scope, and
 * recipient authorization. The browser neither sends a subscription nor
 * stores event payload data.
 */
export function MoneyRealtimeInvalidationSync() {
  const accessToken = useAuthStore((state) => state.accessToken);
  const hasBusiness = useAuthStore((state) => hasBusinessContext(state.user));
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!MONEY_REALTIME_ENABLED || !accessToken || !hasBusiness) return;

    const revalidate = () => {
      void revalidateActiveMoneyQueries(queryClient);
    };
    const socket = io(REALTIME_ENDPOINT, {
      autoConnect: false,
      auth: { token: accessToken },
      path: REALTIME_PATH,
      reconnection: true,
      transports: ['websocket'],
      upgrade: false,
      withCredentials: false,
    });
    const onInvalidation = createMoneyRealtimeInvalidationHandler(revalidate);
    const onReconnectAttempt = () => refreshSocketAuth(socket);

    socket.on(MONEY_REALTIME_INVALIDATION_EVENT, onInvalidation);
    // `connect` fires after the initial connection and every successful
    // reconnect, closing an event-loss window with an authorized HTTP read.
    socket.on('connect', revalidate);
    socket.io.on('reconnect_attempt', onReconnectAttempt);

    // Refresh immediately and on a fixed cadence. This remains the convergence
    // backstop if the socket is unavailable or a connected delivery is lost.
    revalidate();
    const freshnessTimer = window.setInterval(revalidate, MONEY_REALTIME_FRESHNESS_MS);
    const onFocus = () => {
      if (document.visibilityState === 'visible') revalidate();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', revalidate);
    socket.connect();

    return () => {
      window.clearInterval(freshnessTimer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', revalidate);
      socket.off(MONEY_REALTIME_INVALIDATION_EVENT, onInvalidation);
      socket.off('connect', revalidate);
      socket.io.off('reconnect_attempt', onReconnectAttempt);
      socket.disconnect();
    };
  }, [accessToken, hasBusiness, queryClient]);

  return null;
}
